import { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import {
  Button,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Edit, Users, Star, GitBranch, Menu as MenuIcon, Grid, RefreshCw } from "react-feather";
import { getMerchantListing, syncBranches, syncMenu, syncTables } from "../../utils/apiCalls";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import toast from "react-hot-toast";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader } from "../reporting/ReportingShared";

const Merchant_list = () => {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("userType") === "admin";
  const [isFetching, setIsFetching] = useState(false);
  const [merchantData, setMerchantData] = useState([]);
  const [loyaltyModal, setLoyaltyModal] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState(null);
  const [loyaltyValue, setLoyaltyValue] = useState("1%");
  const [syncing, setSyncing] = useState({});
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");

  const handleEditClick = (merchantID) => {
    navigate(`/merchant/edit-merchant/${merchantID}`);
  };

  const handleUserStoreClick = (merchantID) => {
    navigate(`/merchant/user-store/${merchantID}`);
  };

  const toggleLoyaltyModal = (myData) => {
    setCurrentMerchant(myData);
    setLoyaltyValue(myData?.loyalty_base_percentage || "1%");
    setLoyaltyModal(!loyaltyModal);
  };

  const handleLoyaltySave = async () => {
    if (!currentMerchant) return;
    try {
      const merchantID = currentMerchant.id;
      await axiosInstance.post(`/api/merchant/updateMerchant/${merchantID}`, {
        loyalty_base_percentage: loyaltyValue,
      });
      successToast("Loyalty value updated successfully!");
      setMerchantData((prev) =>
        prev.map((m) =>
          m.id === merchantID ? { ...m, loyalty_base_percentage: loyaltyValue } : m
        )
      );
    } catch (error) {
      console.error("Error updating loyalty value:", error);
      toast.error("Failed to update loyalty value.");
    } finally {
      setLoyaltyModal(false);
    }
  };

  const fetchMerchants = async () => {
    setIsFetching(true);
    try {
      const data = await getMerchantListing();
      setMerchantData(data || []);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  const runSync = async (merchantId, reference, kind) => {
    if (!reference) return;
    const key = `${merchantId}:${kind}`;
    setSyncing((s) => ({ ...s, [key]: true }));
    try {
      if (kind === "branches") await syncBranches(reference);
      else if (kind === "menu") await syncMenu(reference);
      else if (kind === "tables") await syncTables(reference);
    } finally {
      setSyncing((s) => ({ ...s, [key]: false }));
    }
  };

  const filtered = (merchantData || []).filter((m) => {
    if (sourceFilter !== "all" && (m?.source || "external") !== sourceFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${m?.name || ""} ${m?.email || ""} ${m?.reference || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const total = merchantData?.length || 0;
  const activeCount = merchantData?.filter((m) => m?.status === 1).length || 0;
  const inactiveCount = total - activeCount;
  const internalCount = merchantData?.filter((m) => m?.source === "internal").length || 0;
  const externalCount = merchantData?.filter((m) => (m?.source || "external") === "external").length || 0;

  const ActionIcon = ({ icon: Icon, color, title, onClick, spinning, disabled }) => (
    <div
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s",
        border: `1px solid ${C.border}`,
        background: C.white,
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = C.coralLight;
        e.currentTarget.style.borderColor = C.coral;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.white;
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <Icon size={15} color={color} className={spinning ? "rpt-spin" : ""} />
    </div>
  );

  const StatusPill = ({ active }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: active ? C.greenLight : C.coralLight,
        color: active ? C.green : C.coral,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: active ? C.green : C.coral,
        }}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );

  const SourceBadge = ({ source }) => {
    const s = source || "external";
    const colors = s === "internal"
      ? { bg: C.blueLight, fg: C.blue }
      : { bg: C.purpleLight, fg: C.purple };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          background: colors.bg,
          color: colors.fg,
        }}
      >
        {s.toUpperCase()}
      </span>
    );
  };

  const filterSelectStyle = {
    padding: "7px 12px",
    fontSize: 12,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    cursor: "pointer",
    minWidth: 140,
    transition: "border 0.15s",
  };

  const searchInputStyle = {
    ...filterSelectStyle,
    cursor: "text",
    minWidth: 220,
  };

  return (
    <Fragment>
      <Breadcrumb title="Merchant List" parent="Merchant" />
      <Container fluid>
        <style>
          {reportingGlobalCSS}
          {`@keyframes rptSpin { to { transform: rotate(360deg); } } .rpt-spin { animation: rptSpin 0.8s linear infinite; }`}
        </style>
        <div className="rpt-wrap">
          {/* KPI Tiles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <Tile label="Total Merchants" value={total} icon="🏢" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Active" value={activeCount} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
            <Tile label="Inactive" value={inactiveCount} icon="⛔" gradient={`linear-gradient(135deg, #E74C3C, #FF6B6B)`} delay={120} />
            <Tile label="Internal" value={internalCount} icon="🏠" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={180} />
            <Tile label="External" value={externalCount} icon="🌐" gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`} delay={240} />
          </div>

          {/* Filter bar + table */}
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={fetchMerchants}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  background: C.white,
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.coral;
                  e.currentTarget.style.background = C.coralLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.background = C.white;
                }}
                title="Refresh"
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <input
                placeholder="Search name, email, reference..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={searchInputStyle}
              />

              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                style={filterSelectStyle}
              >
                <option value="all">All Sources</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>

              {isAdmin && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => navigate("/merchant/add-merchant")}
                  style={{
                    marginLeft: "auto",
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: `linear-gradient(135deg, ${C.coral} 0%, #00bfa5 100%)`,
                    border: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: 0.3,
                    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
                  }}
                >
                  + Add Merchant
                </Button>
              )}

              <span
                style={{
                  marginLeft: isAdmin ? 0 : "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {filtered.length} shown
              </span>
            </div>

            {isFetching ? (
              <RLoader />
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Merchant</th>
                      <th style={tbl.th}>Email</th>
                      <th style={tbl.th}>Reference</th>
                      <th style={tbl.th}>Source</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length > 0 ? (
                      filtered.map((m) => (
                        <tr key={m.id} className="rpt-row">
                          <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600 }}>{m?.id}</td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <img
                                alt={m?.name}
                                src={
                                  m?.image1
                                    ? process.env.REACT_APP_URL + m.image1
                                    : "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png"
                                }
                                onError={(e) => {
                                  e.target.src =
                                    "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png";
                                }}
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 10,
                                  objectFit: "cover",
                                  border: `1px solid ${C.borderLight}`,
                                }}
                              />
                              <div
                                style={{
                                  fontWeight: 700,
                                  color: C.text,
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={m?.name}
                              >
                                {m?.name || "--"}
                              </div>
                            </div>
                          </td>
                          <td style={{ ...tbl.td, color: C.textMed }}>{m?.email || "--"}</td>
                          <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{m?.reference || "--"}</td>
                          <td style={tbl.td}><SourceBadge source={m?.source} /></td>
                          <td style={tbl.td}><StatusPill active={m?.status === 1} /></td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                              {isAdmin && (
                                <ActionIcon icon={Edit} color={C.green} title="Edit" onClick={() => handleEditClick(m?.id)} />
                              )}
                              <ActionIcon icon={Users} color={C.blue} title="User Store" onClick={() => handleUserStoreClick(m?.id)} />
                              <ActionIcon icon={Star} color={C.orange} title="Loyalty Base" onClick={() => toggleLoyaltyModal(m)} />
                              <ActionIcon
                                icon={GitBranch}
                                color={C.textMed}
                                title="Sync Branches"
                                spinning={syncing[`${m?.id}:branches`]}
                                disabled={syncing[`${m?.id}:branches`]}
                                onClick={() => runSync(m?.id, m?.reference, "branches")}
                              />
                              <ActionIcon
                                icon={MenuIcon}
                                color={C.blue}
                                title="Sync Menu"
                                spinning={syncing[`${m?.id}:menu`]}
                                disabled={syncing[`${m?.id}:menu`]}
                                onClick={() => runSync(m?.id, m?.reference, "menu")}
                              />
                              <ActionIcon
                                icon={Grid}
                                color={C.purple}
                                title="Sync Tables"
                                spinning={syncing[`${m?.id}:tables`]}
                                disabled={syncing[`${m?.id}:tables`]}
                                onClick={() => runSync(m?.id, m?.reference, "tables")}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}
                        >
                          No merchants found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>

      {/* Loyalty Modal */}
      <Modal isOpen={loyaltyModal} toggle={toggleLoyaltyModal} centered>
        <ModalHeader toggle={toggleLoyaltyModal} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Update Loyalty Base</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Loyalty Percentage
          </div>
          <Input
            type="text"
            value={loyaltyValue}
            onChange={(e) => setLoyaltyValue(e.target.value)}
            style={{ borderRadius: 8, border: `1.5px solid ${C.border}`, padding: "10px 12px" }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={handleLoyaltySave}
            style={{
              background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
              border: "none",
              borderRadius: 8,
              padding: "8px 20px",
              fontWeight: 600,
            }}
          >
            Save
          </Button>
          <Button color="secondary" onClick={toggleLoyaltyModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Merchant_list;
